import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { Button as ShadcnButton } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TableOutlineDeleteIconButton } from "@/components/ui/table-outline-delete-button";
import { cn } from "@/lib/utils";
import { TRADE_LOG_FILTER_INPUT_CLASS } from "@/components/trade-filter-selects";
import { useTheme } from "next-themes";
import type { TradePolicyCatalogField, TradePolicyMetric } from "../../../types/tradePolicy";
import { formatSimulatePolicySummary } from "./policyTreeUtils";
import { BaselineFilteredMetrics } from "./MetricColumn";
import {
  formatSimulatedLegsBadge,
  exitPlacementSliceRecapLabels,
} from "../../../utils/exitPlacementSimLegs";
import {
  getComparisonRunSavedTitle,
  MAX_COMPARISON_RUN_TITLE_LENGTH,
  type ComparisonRun,
} from "./simulatorComparisonTypes";
import { SimulatorAlert } from "./SimulatorAlert";
import { ResearchDisclaimerNotice } from "../../../components/ResearchDisclaimerNotice";
import { dispatchWorkspaceSimulationPolicyPreviewOpen } from "../Workspace/workspaceSimulationPolicyPreviewEvents";

// Chakra polymorphic props can trigger “union too complex” IDE diagnostics in big TSX trees.
const FlexAny = Flex as unknown as React.FC<any>;
const StackAny = Stack as unknown as React.FC<any>;
const VStackAny = VStack as unknown as React.FC<any>;
const ButtonAny = Button as unknown as React.FC<any>;
const HStackAny = HStack as unknown as React.FC<any>;
const ModalAny = Modal as unknown as React.FC<any>;
const ModalOverlayAny = ModalOverlay as unknown as React.FC<any>;
const ModalContentAny = ModalContent as unknown as React.FC<any>;
const ModalHeaderAny = ModalHeader as unknown as React.FC<any>;
const ModalCloseButtonAny = ModalCloseButton as unknown as React.FC<any>;
const ModalBodyAny = ModalBody as unknown as React.FC<any>;

type ComparisonRunPanelProps = {
  run: ComparisonRun;
  fieldsByPath: Map<string, TradePolicyCatalogField>;
  /** Shared baseline metrics (same source as former left column). */
  sharedBaseline: TradePolicyMetric | undefined;
  borderColor: string;
  textColor: string;
  subtleText: string;
  evidencePreBg: string;
  evidencePreBorder: string;
  onRemove: () => void;
  onSaveRunLabel: (label: string) => void;
  onRetry: () => void;
  onSaveBacktest: () => void;
  onSaveAsStrategyVersion: () => void;
  canSaveAsStrategyVersion: boolean;
  /** Exit Placement: open compose-to-TradingView dialog for this run. */
  onComposeExitPlacement?: () => void;
  canComposeExitPlacement?: boolean;
  /** Saved-simulation detail page — hide redundant save simulation affordance. */
  hideSaveSimulationAction?: boolean;
  /** Entity title card shows the run name — hide the inline title editor. */
  hideRunTitle?: boolean;
  /** Move Show policy into an external chrome row (workspace drawer). */
  hideShowPolicyButton?: boolean;
  /** Move Save as policy version into an external chrome row (workspace drawer). */
  hideSaveAsStrategyVersionButton?: boolean;
  /** Register the open-policy handler for an external Show policy button. */
  onRegisterOpenPolicyModal?: (open: (() => void) | null) => void;
};

export function ComparisonRunPanel({
  run,
  fieldsByPath,
  sharedBaseline,
  borderColor,
  textColor,
  subtleText,
  evidencePreBg,
  evidencePreBorder,
  onRemove,
  onSaveRunLabel,
  onRetry,
  onSaveBacktest,
  onSaveAsStrategyVersion,
  canSaveAsStrategyVersion,
  onComposeExitPlacement,
  canComposeExitPlacement,
  hideSaveSimulationAction = false,
  hideRunTitle = false,
  hideShowPolicyButton = false,
  hideSaveAsStrategyVersionButton = false,
  onRegisterOpenPolicyModal,
}: ComparisonRunPanelProps): React.ReactElement {
  const { resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  const policyModalBg = isDarkTheme ? "#151517" : "white";
  const policyModalBorder = isDarkTheme ? "gray.600" : "gray.200";
  const { isOpen: policyModalOpen, onOpen: onPolicyModalOpen, onClose: onPolicyModalClose } = useDisclosure();
  const [titleDraft, setTitleDraft] = useState(() => getComparisonRunSavedTitle(run));
  const [titleFocused, setTitleFocused] = useState(false);
  const titleRowRef = useRef<HTMLDivElement>(null);

  const policySummary = useMemo(
    () => formatSimulatePolicySummary(run.policy, fieldsByPath),
    [run.policy, fieldsByPath]
  );

  const openPolicyPreview = useCallback(() => {
    // Always open the in-sheet modal so the policy is visible while reviewing results.
    // Workspace canvas may also mount a side dock; that is complementary, not a substitute.
    onPolicyModalOpen();
    if (hideShowPolicyButton) {
      const simulationId = (run.backtestId || run.id || "").trim();
      if (!simulationId) return;
      dispatchWorkspaceSimulationPolicyPreviewOpen({
        simulationId,
        title: getComparisonRunSavedTitle(run),
        policy: run.policy,
      });
    }
  }, [hideShowPolicyButton, onPolicyModalOpen, run]);

  useEffect(() => {
    if (!onRegisterOpenPolicyModal) return;
    onRegisterOpenPolicyModal(openPolicyPreview);
    return () => onRegisterOpenPolicyModal(null);
  }, [onRegisterOpenPolicyModal, openPolicyPreview]);

  const savedTitle = getComparisonRunSavedTitle(run);

  useEffect(() => {
    if (titleFocused) return;
    setTitleDraft(savedTitle);
  }, [run.id, run.label, run.savedTitle, run.pickTabHint, run.titleParseMode, savedTitle, titleFocused]);
  const titleDirty = titleDraft.trim() !== savedTitle.trim();

  const commitTitleEdit = useCallback(() => {
    const next = titleDraft.trim().slice(0, MAX_COMPARISON_RUN_TITLE_LENGTH);
    if (!next || next === savedTitle.trim()) return;
    onSaveRunLabel(next);
    setTitleFocused(false);
  }, [onSaveRunLabel, savedTitle, titleDraft]);

  const onTitleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const next = e.relatedTarget;
    if (next instanceof Node && titleRowRef.current?.contains(next)) return;
    setTitleFocused(false);
  }, []);

  const onTitleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commitTitleEdit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        setTitleDraft(savedTitle);
        setTitleFocused(false);
      }
    },
    [commitTitleEdit, savedTitle]
  );
  const simResult = run.result;
  const isRunning = (run.status ?? (simResult == null && !run.error ? "running" : "succeeded")) === "running";
  const awaitingResults = isRunning && simResult == null;
  const coverage = simResult?.coverage;
  const missingEntryBars = coverage?.trades_missing_entry_bar;
  const tradesInScope = coverage?.trades_total;
  const missingRatio =
    tradesInScope != null && tradesInScope > 0 && missingEntryBars != null ? missingEntryBars / tradesInScope : 0;
  const coverageIssueMessage = useMemo(() => {
    if (!coverage) return null;
    if (tradesInScope === 0) return "No closed trades in the selected range.";
    if (missingEntryBars != null && missingEntryBars > 0 && tradesInScope != null && tradesInScope > 0) {
      const missingLabel = missingEntryBars.toLocaleString();
      const totalLabel = tradesInScope.toLocaleString();
      if (missingRatio > 0.25) {
        return `${missingLabel} of ${totalLabel} in-scope trades are missing entry candles — results may reflect data gaps.`;
      }
      return `${missingLabel} of ${totalLabel} in-scope trades are missing entry candles.`;
    }
    return null;
  }, [coverage, missingEntryBars, missingRatio, tradesInScope]);
  const isBacktestSaved = Boolean(run.isSavedBacktest);
  const isStrategyVersionSaved = Boolean(run.isSavedStrategyVersion);

  /** Always reserve enough width for a full max-length title (dynamic `len+2` ch clipped uppercase text). */
  const titleInputMaxWidthCh = MAX_COMPARISON_RUN_TITLE_LENGTH + 12;

  const simulatedLegsBadge = formatSimulatedLegsBadge(run.simulatedLegs);
  const exitPlacementRecap = exitPlacementSliceRecapLabels(run.exitPlacementSlices);

  const runToolbar = (
    <FlexAny align="center" gap={2} wrap="wrap" w="full" data-simulator-run-toolbar="">
      {!hideRunTitle ? (
        <div
          ref={titleRowRef}
          className="flex min-w-[16rem] max-w-full flex-1 items-center gap-2"
          style={{ maxWidth: `${titleInputMaxWidthCh}ch` }}
        >
          <div data-trade-filter-bar="" data-trade-filter-bar-layout="full" className="min-w-0 flex-1">
            <Input
              value={titleFocused ? titleDraft : savedTitle}
              onChange={(e) => setTitleDraft(e.target.value.slice(0, MAX_COMPARISON_RUN_TITLE_LENGTH))}
              onFocus={() => {
                setTitleDraft(savedTitle);
                setTitleFocused(true);
              }}
              onBlur={onTitleBlur}
              onKeyDown={onTitleKeyDown}
              maxLength={MAX_COMPARISON_RUN_TITLE_LENGTH}
              placeholder="Simulation title"
              aria-label="Simulation title"
              className={cn(TRADE_LOG_FILTER_INPUT_CLASS, "min-w-0")}
            />
          </div>
          {titleFocused ? (
            <ShadcnButton
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
              onMouseDown={(e) => e.preventDefault()}
              onClick={commitTitleEdit}
              disabled={!titleDirty || titleDraft.trim() === ""}
              aria-label="Save simulation title"
            >
              Save
            </ShadcnButton>
          ) : null}
        </div>
      ) : null}
      {!isRunning && (run.jobId || run.backtestId) ? (
        <HStackAny spacing={2} flexWrap="wrap" flexShrink={0}>
          {!hideSaveSimulationAction ? (
            <ShadcnButton
              type="button"
              variant={isBacktestSaved ? "outline" : "gradient"}
              size="sm"
              data-font="primary"
              {...(isBacktestSaved ? { "data-market-context-bulk-toggle": "" } : {})}
              className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
              onClick={onSaveBacktest}
              disabled={isBacktestSaved || !run.jobId}
              title={
                isBacktestSaved
                  ? "This run is already saved as a simulation"
                  : "Save this completed run as a simulation"
              }
            >
              {isBacktestSaved ? "Simulation saved" : "Save simulation"}
            </ShadcnButton>
          ) : null}
          {onComposeExitPlacement ? (
            <ShadcnButton
              type="button"
              variant="outline"
              size="sm"
              data-font="primary"
              data-market-context-bulk-toggle=""
              className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
              onClick={onComposeExitPlacement}
              disabled={!canComposeExitPlacement}
              title={
                canComposeExitPlacement
                  ? "Merge exit management (and regime entry gates when present) into linked strategy Pinescript"
                  : "Link a strategy and complete a simulation with slice ids to compose"
              }
            >
              Compose to TradingView
            </ShadcnButton>
          ) : null}
        </HStackAny>
      ) : null}
      <HStackAny spacing={2} flexWrap="wrap" flexShrink={0} ml="auto" justify="flex-end">
        {!hideShowPolicyButton ? (
          <ShadcnButton
            type="button"
            variant="outline"
            size="sm"
            data-font="primary"
            data-market-context-bulk-toggle=""
            className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={openPolicyPreview}
          >
            Show policy
          </ShadcnButton>
        ) : null}
        {!isRunning &&
        !hideSaveAsStrategyVersionButton &&
        !isStrategyVersionSaved &&
        (run.jobId || run.backtestId) ? (
          <ShadcnButton
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={onSaveAsStrategyVersion}
            disabled={!canSaveAsStrategyVersion}
            title={
              !canSaveAsStrategyVersion
                ? isBacktestSaved
                  ? "Link this report or simulation to a strategy to save a policy version"
                  : "Save simulation first, then save as a policy version"
                : "Save these rules as a new policy version"
            }
          >
            Save as policy version
          </ShadcnButton>
        ) : null}
        {!hideRunTitle ? (
          <TableOutlineDeleteIconButton
            marker="simulator"
            aria-label="Remove this run from comparison"
            onClick={onRemove}
          />
        ) : null}
      </HStackAny>
    </FlexAny>
  );

  return (
    <Box w="100%" minW={0} data-simulator-results-run="">
      <StackAny spacing={3} align="stretch">
        {runToolbar}

        {run.error ? (
          <SimulatorAlert tone="error">{run.error}</SimulatorAlert>
        ) : null}

        {!run.error && !isRunning ? <ResearchDisclaimerNotice kind="simulator" /> : null}

        {!run.error && !isRunning && coverageIssueMessage ? (
          <SimulatorAlert tone={missingRatio > 0.25 ? "warning" : "info"}>
            {coverageIssueMessage}
          </SimulatorAlert>
        ) : null}

        {awaitingResults ? (
          <Box
            borderWidth="1px"
            borderRadius="lg"
            borderColor={borderColor}
            py={{ base: 10, md: 14 }}
            px={4}
          >
            <VStackAny spacing={4} align="center" justify="center" textAlign="center">
              <Spinner size="lg" thickness="3px" color="#38BBD1" speed="0.85s" />
              <Text
                fontSize={{ base: "lg", md: "xl" }}
                fontWeight="normal"
                color="#38BBD1"
                fontFamily="'Urbanist', sans-serif"
                lineHeight="1.2"
              >
                Simulation in progress…
              </Text>
              <Text fontSize="sm" color={subtleText} maxW="md" lineHeight="1.5">
                {run.stillRunning
                  ? "This run is still computing on the server. Leave this tab open—results will appear when the job completes, or check again now."
                  : "Metrics and comparison details will load here as soon as this run finishes."}
              </Text>
              {run.stillRunning ? (
                <ButtonAny size="sm" variant="outline" colorScheme="orange" onClick={onRetry}>
                  Retry check
                </ButtonAny>
              ) : null}
            </VStackAny>
          </Box>
        ) : simResult ? (
          <>
            {simulatedLegsBadge || exitPlacementRecap.length ? (
              <Box fontSize="xs" color={subtleText} lineHeight="1.5">
                {simulatedLegsBadge ? (
                  <Text as="span" fontWeight="medium" color={textColor} mr={2}>
                    {simulatedLegsBadge}
                  </Text>
                ) : null}
                {exitPlacementRecap.length ? (
                  <Text as="span">{exitPlacementRecap.join(" · ")}</Text>
                ) : null}
              </Box>
            ) : null}
            <BaselineFilteredMetrics
              baseline={sharedBaseline}
              filtered={simResult.filtered}
              delta={simResult.delta}
              result={simResult}
              borderColor={borderColor}
              textColor={textColor}
              subtleColor={subtleText}
            />
          </>
        ) : null}
      </StackAny>

      <ModalAny isOpen={policyModalOpen} onClose={onPolicyModalClose} isCentered scrollBehavior="inside" size="lg">
          <ModalOverlayAny />
          <ModalContentAny
            bg={policyModalBg}
            border="1px solid"
            borderColor={policyModalBorder}
            borderRadius="12px"
            maxW="720px"
          >
            <ModalHeaderAny
              fontFamily="'Urbanist', sans-serif"
              color="#38BBD1"
              fontWeight="normal"
              pb={2}
              pr={10}
            >
              Policy
            </ModalHeaderAny>
            <ModalCloseButtonAny />
            <ModalBodyAny pt={0} px={6} pb={6}>
              <Box
                as="pre"
                fontSize="xs"
                fontFamily="mono"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
                p={3}
                borderRadius="12px"
                borderWidth="1px"
                borderColor={evidencePreBorder}
                bg={evidencePreBg}
                color={textColor}
                m={0}
                maxH="60vh"
                overflowY="auto"
              >
                {policySummary?.trim()
                  ? policySummary
                  : "No policy rules were stored on this simulation."}
              </Box>
            </ModalBodyAny>
          </ModalContentAny>
        </ModalAny>
    </Box>
  );
}
